import React from 'react';
import { reportError } from '../../lib/errorReporter';

interface AppErrorBoundaryProps {
  name: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
  /**
   * 이 값이 바뀌면 에러 상태를 자동으로 초기화한다.
   * 라우트 pathname 같이 "위치가 바뀌면 더 이상 유효하지 않은 에러" 를 리셋하기 위해 사용.
   */
  resetKey?: unknown;
}

interface AppErrorBoundaryState {
  hasError: boolean;
  resetKey: unknown;
}

const defaultFallbackStyle: React.CSSProperties = {
  height: '100%',
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  textAlign: 'center',
  color: '#6b7280',
};

export class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { hasError: false, resetKey: undefined };

  static getDerivedStateFromError(): Partial<AppErrorBoundaryState> {
    return { hasError: true };
  }

  /**
   * 부모가 넘긴 resetKey 가 변경되면 자동으로 에러 상태를 해제한다.
   * 라우터 pathname 이 바뀌면 기존 에러는 더 이상 유효하지 않으므로 사용자에게
   * "문제가 발생했습니다" 화면을 계속 보여주지 않고 새 라우트를 시도하도록 한다.
   */
  static getDerivedStateFromProps(
    props: AppErrorBoundaryProps,
    state: AppErrorBoundaryState,
  ): Partial<AppErrorBoundaryState> | null {
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportError(error, {
      tags: {
        boundary: this.props.name,
      },
      extra: {
        componentStack: info.componentStack,
      },
    });
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;

    return (
      <div style={defaultFallbackStyle}>
        문제가 발생했습니다. 페이지를 새로고침한 뒤 다시 시도해주세요.
      </div>
    );
  }
}
